import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ComicCard from "@/components/feature/ComicCard";
import { categories, comics, getCategoryById } from "@/mocks/comics";

const jsonLd = (name: string, description: string) => ({
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  name: `${name}漫画分类 - 漫画岛`,
  description,
  breadcrumb: {
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://rejqj97.cn/" },
      { "@type": "ListItem", position: 2, name: "漫画分类", item: "https://rejqj97.cn/category/all" },
    ],
  },
});

export default function Category() {
  const { category = "all" } = useParams();
  const active = getCategoryById(category);
  const activeName = active ? active.name : "全部";

  const filtered = useMemo(() => {
    if (category === "all") return comics;
    return comics.filter((c) => c.category === category);
  }, [category]);

  const list = useMemo(() => {
    const sorted = [...filtered];
    const hot = sorted.filter((c) => c.hot);
    const rest = sorted.filter((c) => !c.hot);
    return [...hot, ...rest];
  }, [filtered]);

  const desc = active ? active.description : "浏览漫画岛全部高清漫画,支持横屏观看与夜间模式阅读。";

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd(activeName, desc)) }}
      />
      <Navbar />

      <main className="bg-background-100 pt-16">
        <section className="bg-primary-500">
          <div className="container-site py-12 text-center md:py-14">
            <h1 className="font-heading text-3xl text-background-50 md:text-4xl">
              {activeName}漫画
            </h1>
            <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-white/85 md:text-base">
              {desc}
            </p>
          </div>
        </section>

        <nav aria-label="面包屑" className="container-site py-4">
          <ol className="flex flex-wrap items-center gap-1.5 text-sm text-foreground-700">
            <li>
              <Link to="/" className="whitespace-nowrap hover:text-primary-500">首页</Link>
            </li>
            <li aria-hidden="true"><i className="ri-arrow-right-s-line"></i></li>
            <li>
              <Link to="/category/all" className="whitespace-nowrap hover:text-primary-500">漫画分类</Link>
            </li>
            <li aria-hidden="true"><i className="ri-arrow-right-s-line"></i></li>
            <li className="whitespace-nowrap font-semibold text-primary-600" aria-current="page">
              {activeName}
            </li>
          </ol>
        </nav>

        <section className="container-site pb-4">
          <div className="flex flex-wrap gap-2 rounded-lg bg-background-50 p-2">
            <Link
              to="/category/all"
              className={`whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                category === "all"
                  ? "bg-primary-500 font-semibold text-background-50"
                  : "text-foreground-800 hover:bg-background-100"
              }`}
            >
              全部
            </Link>
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.id}`}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                  category === cat.id
                    ? "bg-primary-500 font-semibold text-background-50"
                    : "text-foreground-800 hover:bg-background-100"
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>
        </section>

        <section className="container-site py-8 md:py-10">
          <div className="mb-5 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-foreground-950 md:text-xl">
              {activeName}漫画 <span className="text-sm font-normal text-foreground-500">共 {list.length} 部</span>
            </h2>
          </div>

          {list.length === 0 ? (
            <div className="rounded-lg bg-background-50 py-20 text-center">
              <i className="ri-inbox-2-line text-4xl text-foreground-300"></i>
              <p className="mt-4 text-sm text-foreground-600">该分类下暂无漫画,去其他分类看看吧。</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-5">
              {list.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          )}
        </section>
      </main>

      <Footer />
    </>
  );
}